export const MIX_WEIGHT_KEYS = [
  "frequency",
  "instruments",
  "vocals",
  "style",
  "mimic",
  "seed",
  "healing",
] as const;

export type MixWeightKey = (typeof MIX_WEIGHT_KEYS)[number];
export type MixWeights = Record<MixWeightKey, number>;

export const DEFAULT_MIX_WEIGHTS: MixWeights = {
  frequency: 55,
  instruments: 70,
  vocals: 70,
  style: 55,
  mimic: 0,
  seed: 0,
  healing: 0,
};

const KEY = "musicforge.mixWeights.v1";

export function clampWeight(n: number): number {
  return Math.max(0, Math.min(100, Math.round(Number(n) || 0)));
}

export function loadMixWeights(): MixWeights {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_MIX_WEIGHTS };
    const parsed = JSON.parse(raw) as Partial<MixWeights>;
    const next = { ...DEFAULT_MIX_WEIGHTS };
    for (const k of MIX_WEIGHT_KEYS) {
      if (typeof parsed[k] === "number") next[k] = clampWeight(parsed[k] as number);
    }
    return next;
  } catch {
    return { ...DEFAULT_MIX_WEIGHTS };
  }
}

export function saveMixWeights(w: MixWeights): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(w));
  } catch {
    /* */
  }
}

export function lanePhrase(name: string, weight: number, body: string): string {
  const w = clampWeight(weight);
  if (w <= 0) {
    return `${name} lane at 0/100 — ignore this lane. Do not let it overwrite or dominate the other lanes.`;
  }
  const blend =
    w < 35
      ? "Treat as a light tint under the other lanes, not a takeover."
      : w > 75
        ? "This lane may lead, but still blend; do not erase Frequency, Mimic, Healing, or the others that have weight."
        : "Blend with the other weighted lanes. Do not overwrite them.";
  return `${name} lane at ${w}/100. ${blend} ${body}`.trim();
}
